import { HALLIDAY_DOMAIN, WIDGET_WIDTH } from "@shared/constants";
import { isMobile } from "@shared/utils/browserUtils";
import { InitConfig, PaymentsWidgetQueryParams, SDKOverrides, MessageType, AppMode } from "./types";
import { logError, getPaymentsWidgetUrl, removeEmbedSkeleton, EMBED_INITIAL_HEIGHT } from "./utils";

/**
 * Internal state of the preloaded widget.
 * Not exported - this is implementation detail.
 */
type PreloadState = {
    isPreloaded: boolean;
    isLoading: boolean;
    iframe: HTMLIFrameElement | null;
    error: Error | null;
    startTime: number | null;
    readyTime: number | null;
};

// ============================================
// State Management
// ============================================

const PRELOAD_IFRAME_ID = "halliday-payments-preload";
const INIT_TIMEOUT_MS = 30000; // 30 seconds timeout

let _state: PreloadState = {
    isPreloaded: false,
    isLoading: false,
    iframe: null,
    error: null,
    startTime: null,
    readyTime: null,
};

let _initConfig: InitConfig | null = null;
let _readyCallback: (() => void) | null = null;
let _errorCallback: ((error: Error) => void) | null = null;
let _overrides: SDKOverrides | null = null;
let _resizeHandler: ((event: MessageEvent) => void) | null = null;
let _lastKnownHeight: number | null = null;
let _initPromise: Promise<void> | null = null;
let _cancelInit: ((reason?: Error) => void) | null = null;
let _initRunId = 0;

/**
 * Check if the widget is initialized and ready
 */
export const isPreloaded = (): boolean => _state.isPreloaded && !_state.error;

/**
 * Check if init has been called
 */
export const isInitialized = (): boolean => _initConfig !== null;

/**
 * Get the stored init config (internal use)
 */
export const getInitConfig = (): InitConfig | null => _initConfig;

/**
 * Get the stored overrides (internal use)
 */
export const getOverrides = (): SDKOverrides | null => _overrides;

// ============================================
// Init Function
// ============================================

/**
 * Initialize and preload the Halliday Payments widget.
 *
 * This loads the widget in a hidden iframe so that when you call
 * `openHallidayPayments()`, it appears instantly - no loading, no delay.
 *
 * @param config Configuration with apiKey and optional callbacks
 * @returns Promise that resolves when widget is fully loaded and ready
 *
 * @example
 * ```javascript
 * // Initialize early in your app
 * await initializeClient({
 *   apiKey: 'your-api-key',
 *   onReady: () => console.log('Widget fully loaded!'),
 *   onError: (err) => console.error('Failed to load:', err),
 * });
 *
 * // Later, when user clicks "Pay" - opens INSTANTLY
 * openHallidayPayments({
 *   apiKey: 'your-api-key',
 *   windowType: 'MODAL',
 *   outputs: ['ethereum:usdc'],
 * });
 * ```
 */
export const initializeClient = async (config: InitConfig, ...args: any[]): Promise<void> => {
    // Don't re-init if already loaded
    if (_state.isPreloaded) {
        console.warn("[HallidayPayments] Already initialized");
        return;
    }
    const runId = ++_initRunId;
    if (_state.isLoading && _initPromise) {
        const error = new Error("Initialization superseded by a newer call");
        _cancelInit?.(error);
    }

    const overridesArg = args[999] || args[0] || {};

    let validatedConfig: InitConfig;
    try {
        validatedConfig = InitConfig.parse(config) as InitConfig;
    } catch (error) {
        logError(error);
        throw error;
    }

    let validatedOverrides: SDKOverrides;
    try {
        validatedOverrides = SDKOverrides.parse(overridesArg);
    } catch (error) {
        logError(error);
        throw error;
    }

    // Store config and overrides
    _initConfig = validatedConfig;
    _overrides = validatedOverrides;
    _readyCallback = validatedConfig.onReady || null;
    _errorCallback = validatedConfig.onError || null;

    // Minimal defaults - will be overridden by openHallidayPayments
    const windowOrigin = (validatedOverrides.dangerouslyOverrideHallidayDomainName || HALLIDAY_DOMAIN).replace(
        /\/$/,
        "",
    );

    _state = {
        ..._state,
        isLoading: true,
        startTime: Date.now(),
        error: null,
    };

    const initPromise = new Promise<void>((resolve, reject) => {
        let isActive = true;
        let timeoutId: ReturnType<typeof setTimeout> | null = null;

        const cancel = (reason?: Error) => {
            if (!isActive) return;
            isActive = false;
            if (timeoutId) {
                clearTimeout(timeoutId);
                timeoutId = null;
            }
            cleanup();
            if (_state.iframe) {
                _state.iframe.remove();
            }
            _state = {
                ..._state,
                isLoading: false,
                error: null,
                iframe: null,
            };
            finalize();
            reject(reason || new Error("Initialization cancelled"));
        };

        const finalize = () => {
            _initPromise = null;
            if (_cancelInit === cancel) {
                _cancelInit = null;
            }
        };

        // The purpose of this setTimeout is to enforce a maximum time limit (INIT_TIMEOUT_MS) for the widget to finish loading.
        // If the widget does not signal readiness within this period, the timeout handler will execute:
        //  - It creates a "Widget load timeout exceeded" error,
        //  - Updates state to reflect loading failure,
        //  - Cleans up event listeners,
        //  - Calls the error callback (if provided),
        //  - And rejects the promise, notifying the caller initialization failed due to timeout.
        timeoutId = setTimeout(() => {
            if (!isActive || runId !== _initRunId) return;
            const error = new Error("Widget load timeout exceeded");
            _state = {
                ..._state,
                isLoading: false,
                error,
            };
            cleanup();
            _errorCallback?.(error);
            finalize();
            reject(error);
        }, INIT_TIMEOUT_MS);
        if (!isActive && timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
        }

        const handleReady = (event: MessageEvent) => {
            if (!isActive || runId !== _initRunId) return;
            // Verify origin and source
            if (event.origin !== windowOrigin) return;
            if (event.source !== _state.iframe?.contentWindow) return;

            const { type } = event.data || {};

            // Widget signals it's fully loaded and ready
            if (type === "WIDGET_READY" || type === "HALLIDAY_READY") {
                if (timeoutId) {
                    clearTimeout(timeoutId);
                    timeoutId = null;
                }
                cleanup();

                _state = {
                    ..._state,
                    isPreloaded: true,
                    isLoading: false,
                    readyTime: Date.now(),
                };

                _readyCallback?.();
                finalize();
                resolve();
            }
        };

        // Handle resize messages for EMBED mode (set up early so we don't miss any)
        const handleResize = (event: MessageEvent) => {
            if (!isActive || runId !== _initRunId) return;
            if (event.origin !== windowOrigin) return;
            if (event.source !== _state.iframe?.contentWindow) return;

            const { type, payload } = event.data || {};
            if (type === MessageType.EVENT_RESIZE && payload && _state.iframe) {
                if (payload.width !== undefined) {
                    _state.iframe.style.width = payload.width + "px";
                }
                if (payload.height !== undefined) {
                    _state.iframe.style.height = payload.height + "px";
                }
            }
        };

        const cleanup = () => {
            window.removeEventListener("message", handleReady);
            window.removeEventListener("message", handleResize);
            if (_resizeHandler === handleResize) {
                _resizeHandler = null;
            }
        };

        window.addEventListener("message", handleReady);
        window.addEventListener("message", handleResize);
        _resizeHandler = handleResize;

        // Remove existing iframe if any
        const existingIframe = document.getElementById(PRELOAD_IFRAME_ID);
        if (existingIframe) {
            existingIframe.remove();
        }

        // Create hidden iframe for preloading
        const iframe = document.createElement("iframe");
        iframe.id = PRELOAD_IFRAME_ID;

        // Default appMode for preloading - will be updated via postMessage when openHallidayPayments is called
        let appMode: AppMode = "MODAL";
        try {
            appMode = isMobile() ? "OVERLAY" : "MODAL";
        } catch {
            // OK if isMobile() fails
        }

        // Build minimal widget URL - just enough to preload
        // All business params (outputs, customStyles, etc.) are sent via postMessage at open time
        const widgetUrl = getPaymentsWidgetUrl({
            apiKey: validatedConfig.apiKey,
            sandbox: validatedConfig.sandbox || false,
            widgetVersion: "2", // Always use v2
            // Use outputs from overrides if provided, so widget can fetch payment methods before open
            outputs: [],
            hasOwner: false,
            hasTxHandler: false,
            hostOrigin: typeof window !== "undefined" ? window.origin : null,
            windowOrigin,
            appMode,
            // Testing overrides
            apiBaseUrl: validatedOverrides.dangerouslyOverrideApiBaseUrl,
            ipAddress: validatedOverrides.dangerouslyOverrideIPAddress,
            featureFlags: validatedOverrides.dangerouslyOverrideFeatureFlags,
        });

        iframe.src = widgetUrl;

        // ALL modes: Create hidden iframe in document.body
        // For EMBED mode, we'll position it over the target element when openHallidayPayments is called
        // This allows init to be called before the target element even exists (SPA use case)
        iframe.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100dvw;
            height: 100dvh;
            border: none;
            z-index: -1;
            visibility: hidden;
            pointer-events: none;
            opacity: 0;
        `;
        iframe.setAttribute("aria-hidden", "true");
        iframe.setAttribute("tabindex", "-1");

        // Handle load event as fallback
        iframe.onload = () => {
            if (!isActive || runId !== _initRunId) return;
            // Give the widget a moment to send ready message
            // If no message after 3s, assume it's ready (fallback)
            setTimeout(() => {
                if (!isActive || runId !== _initRunId) return;
                if (_state.isLoading && !_state.isPreloaded) {
                    if (timeoutId) {
                        clearTimeout(timeoutId);
                        timeoutId = null;
                    }
                    cleanup();

                    _state = {
                        ..._state,
                        isPreloaded: true,
                        isLoading: false,
                        readyTime: Date.now(),
                    };

                    _readyCallback?.();
                    finalize();
                    resolve();
                }
            }, 3000);
        };

        // Handle load error
        iframe.onerror = () => {
            if (!isActive || runId !== _initRunId) return;
            if (timeoutId) {
                clearTimeout(timeoutId);
                timeoutId = null;
            }
            cleanup();
            const error = new Error("Failed to load widget");
            _state = {
                ..._state,
                isLoading: false,
                error,
            };
            _errorCallback?.(error);
            finalize();
            reject(error);
        };

        // Append to body for ALL modes (including EMBED with overlay approach)
        document.body.appendChild(iframe);
        _state.iframe = iframe;

        _cancelInit = cancel;
    });
    _initPromise = initPromise;
    return initPromise;
};

// ============================================
// Show/Hide Functions (called by openHallidayPayments)
// ============================================

/**
 * @internal
 * Show the preloaded widget iframe with merged params.
 * Called by openHallidayPayments when widget was initialized via initHallidayPayments.
 *
 * NOTE: windowType comes from initConfig (required at init time), not openParams.
 * For EMBED mode, targetElementId comes from openParams (not known at init time).
 *
 * @param openParams - Params from openHallidayPayments call (merged with init params)
 * @returns Window object, or null if POPUP mode (can't use preloaded iframe for popup)
 */
export const _showWidget = (openParams?: Partial<PaymentsWidgetQueryParams>): Window | null => {
    const iframe = _state.iframe;
    if (!iframe || !_state.isPreloaded || !_initConfig) {
        return null;
    }

    // Remove trailing slash for consistent origin comparison (event.origin never has trailing slash)
    const windowOrigin = (_overrides?.dangerouslyOverrideHallidayDomainName || HALLIDAY_DOMAIN).replace(/\/$/, "");

    // windowType comes from openParams (init no longer has windowType)
    const windowType = openParams?.windowType || "MODAL";

    // For EMBED mode, targetElementId comes from openParams (not init - allows SPA use case)
    const targetElementId = openParams?.targetElementId;

    // POPUP mode cannot use preloaded iframe - return null to fall back to normal flow
    // (window.open() popup cannot be created from an existing iframe)
    if (windowType === "POPUP") {
        return null;
    }

    // Determine appMode based on final windowType
    let appMode: AppMode = "MODAL";
    try {
        if (windowType === "MODAL") {
            appMode = isMobile() ? "OVERLAY" : "MODAL";
        } else if (windowType === "EMBED") {
            appMode = "EMBED";
        }
    } catch {
        // OK if isMobile() fails
    }

    // Send merged params to the widget via HALLIDAY_UPDATE_PARAMS message
    // Include appMode so HallidayApps can update its rendering mode
    if (openParams) {
        const { windowType: _ignored, ...otherParams } = openParams;
        iframe.contentWindow?.postMessage(
            {
                type: "HALLIDAY_UPDATE_PARAMS",
                payload: {
                    ...otherParams,
                    appMode,
                },
            },
            windowOrigin,
        );
    }

    // Handle EMBED mode - use overlay positioning over target element
    if (windowType === "EMBED") {
        if (!targetElementId) {
            console.error("[HallidayPayments] EMBED mode requires targetElementId in openHallidayPayments");
            return null;
        }

        // Find the target element (may not have existed at init time - SPA use case)
        const targetElement = document.getElementById(targetElementId);
        if (!targetElement) {
            console.error(`[HallidayPayments] Target element '${targetElementId}' not found`);
            return null;
        }

        // Use existing wrapper (from skeleton) or create placeholder
        // This ensures the iframe is positioned over the skeleton, not below it
        let placeholder =
            document.getElementById("halliday-payments-wrapper") ||
            document.getElementById("halliday-payments-placeholder");
        if (!placeholder) {
            placeholder = document.createElement("div");
            placeholder.id = "halliday-payments-placeholder";
            placeholder.style.cssText = `
                width: ${WIDGET_WIDTH}px;
                max-width: 100%;
                height: ${_lastKnownHeight || EMBED_INITIAL_HEIGHT}px;
            `;
            targetElement.appendChild(placeholder);
        }

        // Position iframe over the placeholder using fixed positioning
        const updateIframePosition = () => {
            const rect = placeholder!.getBoundingClientRect();
            iframe.style.position = "fixed";
            iframe.style.top = rect.top + "px";
            iframe.style.left = rect.left + "px";
            iframe.style.width = rect.width + "px";
            iframe.style.height = rect.height + "px";
            iframe.style.border = "none";
            iframe.style.zIndex = "1"; // Above normal content, below modals/tooltips
        };

        // Force browser to layout the placeholder before measuring
        // Reading offsetHeight triggers a synchronous reflow
        void placeholder.offsetHeight;

        // Use double-rAF to ensure position is calculated after layout is complete
        // and iframe is made visible in a single paint
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                // Calculate position right before making visible
                updateIframePosition();
                iframe.style.visibility = "visible";
                iframe.style.opacity = "1";
                iframe.style.pointerEvents = "auto";
            });
        });

        // Set up ResizeObserver to keep iframe aligned with placeholder
        const resizeObserver = new ResizeObserver(() => {
            updateIframePosition();
        });
        resizeObserver.observe(placeholder);

        // Also update on scroll (in case target is in a scrollable container)
        const scrollHandler = () => updateIframePosition();
        window.addEventListener("scroll", scrollHandler, true);

        // Update on window resize (container position may change)
        const resizeHandler = () => updateIframePosition();
        window.addEventListener("resize", resizeHandler);

        // Store cleanup functions for destroyPreload
        (iframe as any)._embedCleanup = () => {
            resizeObserver.disconnect();
            window.removeEventListener("scroll", scrollHandler, true);
            window.removeEventListener("resize", resizeHandler);
            placeholder?.remove();
        };

        // Handle resize messages from widget to update placeholder height
        const handleResize = (event: MessageEvent) => {
            if (event.origin !== windowOrigin) return;
            if (event.source !== iframe.contentWindow) return;

            const { type, payload } = event.data || {};
            if (type === MessageType.EVENT_RESIZE && payload) {
                if (payload.height !== undefined && placeholder) {
                    _lastKnownHeight = payload.height;
                    placeholder.style.height = payload.height + "px";
                    updateIframePosition();
                }
            }
        };
        window.addEventListener("message", handleResize);

        iframe.removeAttribute("aria-hidden");
        iframe.removeAttribute("tabindex");

        // Clean up skeleton if widget is already loaded
        if (_state.isPreloaded) {
            removeEmbedSkeleton();
        } else {
            // Wait for widget ready, then fade out skeleton
            const handleSkeletonReady = (event: MessageEvent) => {
                if (event.origin !== windowOrigin) return;
                if (event.source !== iframe.contentWindow) return;

                const { type } = event.data || {};
                if (type === "WIDGET_READY" || type === "HALLIDAY_READY") {
                    window.removeEventListener("message", handleSkeletonReady);
                    removeEmbedSkeleton();
                }
            };
            window.addEventListener("message", handleSkeletonReady);
        }
    } else {
        // MODAL mode - show as full-screen overlay
        iframe.id = "halliday-payments-modal";
        iframe.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100dvw;
            height: 100dvh;
            border: none;
            z-index: 10000;
        `;
        iframe.removeAttribute("aria-hidden");
        iframe.removeAttribute("tabindex");
        iframe.setAttribute("scrolling", "no");

        // Make sure iframe is in body (not in some target element from previous embed)
        if (iframe.parentElement !== document.body) {
            document.body.appendChild(iframe);
        }
    }

    // Tell widget it's now visible (after params are sent)
    // Small delay to ensure params are processed first
    setTimeout(() => {
        iframe.contentWindow?.postMessage({ type: "HALLIDAY_SHOW" }, windowOrigin);
    }, 10);

    // Set up close handler
    const handleClose = (event: MessageEvent) => {
        if (event.origin !== windowOrigin) return;
        if (event.source !== iframe.contentWindow) return;

        const { type } = event.data || {};
        if (type === MessageType.EVENT_WINDOW_CLOSE) {
            window.removeEventListener("message", handleClose);
            _hideWidget();
        }
    };
    window.addEventListener("message", handleClose);

    return iframe.contentWindow;
};

/**
 * @internal
 * Hide the widget iframe (don't destroy, keep for re-use).
 */
export const _hideWidget = (): void => {
    const iframe = _state.iframe;
    if (!iframe) return;

    iframe.style.visibility = "hidden";
    iframe.style.opacity = "0";
    iframe.style.pointerEvents = "none";
    iframe.style.zIndex = "-1";
    iframe.setAttribute("aria-hidden", "true");
    iframe.setAttribute("tabindex", "-1");
};

/**
 * Destroy the preloaded widget and reset state.
 * Call this when you need to re-initialize with different settings.
 */
export const destroyClient = (): void => {
    // Remove resize handler
    if (_resizeHandler) {
        window.removeEventListener("message", _resizeHandler);
        _resizeHandler = null;
    }

    // Call embed cleanup if it exists (removes ResizeObserver, scroll listener, placeholder)
    const iframe = _state.iframe;
    if (iframe && (iframe as any)._embedCleanup) {
        (iframe as any)._embedCleanup();
    }

    // Remove preload iframe
    const preloadIframe = document.getElementById(PRELOAD_IFRAME_ID);
    if (preloadIframe) {
        preloadIframe.remove();
    }

    // Remove embed placeholder (in case cleanup didn't run)
    const placeholder = document.getElementById("halliday-payments-placeholder");
    if (placeholder) {
        placeholder.remove();
    }

    // Remove modal iframe
    const modalIframe = document.getElementById("halliday-payments-modal");
    if (modalIframe) {
        modalIframe.remove();
    }

    // Remove skeleton
    const skeleton = document.getElementById("halliday-payments-skeleton");
    if (skeleton) {
        skeleton.remove();
    }

    // Reset state
    _state = {
        isPreloaded: false,
        isLoading: false,
        iframe: null,
        error: null,
        startTime: null,
        readyTime: null,
    };

    _initConfig = null;
    _overrides = null;
    _readyCallback = null;
    _errorCallback = null;
    _lastKnownHeight = null;
    _initPromise = null;
    _cancelInit = null;
};
